import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { DynamicComponentProps } from '@/types/ModuleComponentProp.type';
import { DecimalInput } from '@/components/DecimalInput';
import { useAqlLevels } from '@/hooks/useAqlLevels';
import { useNetwork } from '@/hooks/useNetwork';
import { InspectionImageType } from '@/enums/InspectionImage.enum';
import { InspectionOwner } from '@/enums/InspectionOwner.enum';
import { imageStorage } from '@/store/idbStorage/imageStorage';
import { savedInspectionsStorageV2 } from '@/store/idbStorage/savedInspectionsStorageV2';
import { generateUniqueId, interpolate } from '@/utils/string.util';
import { requestInspectionAbandon } from '@/utils/abandonInspection';
import { Image } from '@/types/Image.type';
import { ROUTES } from '@/route/routes.constants';
import store from '@/store/store';
import { setInspectionList } from '@/store/slices/inspectionsSlice';
import { closeInspection } from '@/store/slices/openInspectionSlice';
import {
  appendNoPackingListComment,
  countCriticalDefects,
  hasEnteredActualQuantity,
  isActualQuantityLocked,
  removeNoPackingListComment,
  hasQuantityTolerances,
  isActualQuantityWithinTolerance,
  PACKING_LIST_IMAGE_TYPE,
  QUANTITY_FAIL_DEFECT_KEY,
  resolveQuantityEntryPrompt,
  shouldShowSkipQuantityDiscrepancy,
  resolveDeviceSampling,
} from '@/utils/quantity-check.utils';
import { QuantityPrompt } from './QuantityPrompt';
import styles from './QuantityCheckDetails.module.scss';

type ToleranceChoice = 'pending' | 'recalc' | 'keep' | 'wait' | 'fail';

const ABANDON_FAILED_TOAST_ID = 'abandon-inspection-failed';

function isPackingListImage(image: Image): boolean {
  if (image.isDeleted) {
    return false;
  }
  if (image.imageType === PACKING_LIST_IMAGE_TYPE) {
    return true;
  }
  if (!image.url) {
    return false;
  }
  try {
    return new URL(image.url).searchParams.get('imageType') === PACKING_LIST_IMAGE_TYPE;
  } catch {
    return (
      image.url.includes('imageType=Packing%20List') || image.url.includes('imageType=Packing List')
    );
  }
}

function collectPackingListImages(
  inspection: DynamicComponentProps['inspection'] | undefined,
  localImages: Image[]
): Image[] {
  const saved = [...(inspection?.fpImages ?? []), ...(inspection?.newImages ?? [])];
  const imagesById = new Map<string, Image>();
  saved.forEach((image) => {
    if (image?.id && isPackingListImage(image)) {
      imagesById.set(image.id, image);
    }
  });
  localImages.forEach((image) => {
    if (image.id) {
      imagesById.set(image.id, { ...imagesById.get(image.id), ...image });
    }
  });
  return [...imagesById.values()];
}

function withoutPackingListUrls(urls: string | undefined): string {
  if (!urls) {
    return '';
  }
  return urls
    .split(',')
    .map((url) => url.trim())
    .filter(
      (url) =>
        url.length > 0 &&
        !isPackingListImage({ id: url, url, type: InspectionImageType.FULL_PRODUCT })
    )
    .join(',');
}

function savedPackingShouldShow(
  inspection: DynamicComponentProps['inspection'] | undefined
): boolean {
  const actual = hasEnteredActualQuantity(inspection?.actualQuantity)
    ? Number(inspection?.actualQuantity)
    : null;
  const advised = Number(inspection?.lotSize ?? 0);
  if (actual == null || actual === advised) {
    return false;
  }
  const hasQuantity =
    inspection?.packingListQuantity != null && String(inspection.packingListQuantity) !== '';
  return hasQuantity || collectPackingListImages(inspection, []).length > 0;
}

function formatQuantity(value: number): string {
  if (!Number.isFinite(value)) {
    return '';
  }
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

function isNonNegativeWholeQuantity(rawValue: string): boolean {
  const parsed = Number(rawValue);
  return Number.isInteger(parsed) && parsed >= 0;
}

function isWholeQuantity(rawValue: string): boolean {
  return isNonNegativeWholeQuantity(rawValue) && Number(rawValue) > 0;
}

export const QuantityCheckDetails: React.FC<DynamicComponentProps> = ({
  inspection,
  onValueChange,
  onInspectionPatch,
  fullProductImageHandlers,
  defectsChangeHandlers,
}: DynamicComponentProps) => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const isOnline = useNetwork();
  const isEnabled = inspection?.actualQuantityEnabled === true;
  const isQuantityLocked = isActualQuantityLocked(inspection?.inspectionStatusId);
  const aqlRows = useAqlLevels(isEnabled);
  const [actualDraft, setActualDraft] = useState<string | null>(null);
  const [actualError, setActualError] = useState('');
  const [packingDraft, setPackingDraft] = useState<string | null>(null);
  const [packingError, setPackingError] = useState('');
  const [packingPreviews, setPackingPreviews] = useState<Image[]>([]);
  const [showDiscrepancyPrompt, setShowDiscrepancyPrompt] = useState(false);
  const [packingVisible, setPackingVisible] = useState<boolean | null>(null);
  const [reviewTolerance, setReviewTolerance] = useState(false);
  const [toleranceChoice, setToleranceChoice] = useState<ToleranceChoice>('pending');
  const [isAbandoning, setIsAbandoning] = useState(false);
  const [factoryQuantityDraft, setFactoryQuantityDraft] = useState('');
  const [factoryQuantityError, setFactoryQuantityError] = useState('');
  const failDefectBaseline = useRef<number | null>(null);
  const hasOpenedFailDefect = useRef(false);
  const isAddDefectVisible = defectsChangeHandlers?.isAddDefectVisible === true;
  useEffect(() => {
    if (toleranceChoice !== 'fail' || failDefectBaseline.current == null) {
      return;
    }
    if (isAddDefectVisible) {
      hasOpenedFailDefect.current = true;
      return;
    }
    if (!hasOpenedFailDefect.current) {
      return;
    }
    const hasLoggedFailDefect =
      countCriticalDefects(inspection?.qcDefects) > failDefectBaseline.current;
    hasOpenedFailDefect.current = false;
    failDefectBaseline.current = null;
    if (!hasLoggedFailDefect) {
      setToleranceChoice('pending');
    }
  }, [isAddDefectVisible, toleranceChoice, inspection?.qcDefects]);
  const bookedSampling = useRef({
    sampleSize: inspection?.sampleSize,
    allowedDefectsMaj: inspection?.allowedDefectsMaj,
    allowedDefectsMin: inspection?.allowedDefectsMin,
  });
  const capturedInspectionId = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  if (
    inspection?.id &&
    capturedInspectionId.current !== inspection.id &&
    !inspection.sampleQuantityRecalculated
  ) {
    capturedInspectionId.current = inspection.id;
    bookedSampling.current = {
      sampleSize: inspection.sampleSize,
      allowedDefectsMaj: inspection.allowedDefectsMaj,
      allowedDefectsMin: inspection.allowedDefectsMin,
    };
  }
  const advisedQuantity = Number(inspection?.lotSize ?? 0);
  const actualQuantity = inspection?.actualQuantity;
  const actualKey = hasEnteredActualQuantity(actualQuantity) ? Number(actualQuantity) : null;
  const addPackingListPhoto = async (file: File): Promise<void> => {
    const dataUrl = await readFileAsDataUrl(file);
    const image: Image = {
      id: generateUniqueId(),
      name: file.name,
      data: dataUrl,
      isNew: true,
      type: InspectionImageType.FULL_PRODUCT,
      imageType: PACKING_LIST_IMAGE_TYPE,
    };
    if (inspection?.id) {
      await imageStorage.setImage(
        inspection.id,
        InspectionImageType.FULL_PRODUCT,
        image.id,
        dataUrl
      );
    }
    await fullProductImageHandlers?.onAdd?.(image);
    setPackingPreviews((current) => [...current, image]);
  };
  if (!isEnabled) {
    return (
      <div className={styles.formContainer}>
        <div className={styles.inputRow}>
          <div className={styles.inputGroup}>
            <DecimalInput
              id="lotSize"
              name="lotSize"
              value={inspection?.lotSize}
              onChange={() => undefined}
              disabled
              label={translate('Advised Quantity')}
              labelClassName={styles.label}
              className={styles.input}
            />
          </div>
          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="actualQuantity">
              {translate('Actual Quantity')}
            </label>
            <input
              className={styles.input}
              type="number"
              id="actualQuantity"
              name="actualQuantity"
              onChange={onValueChange}
              value={inspection?.actualQuantity ?? ''}
              disabled={isQuantityLocked}
              aria-describedby={isQuantityLocked ? 'actual-quantity-help' : undefined}
            />
            {isQuantityLocked && (
              <p id="actual-quantity-help" className={styles.quantityHint}>
                {translate('Actual_Quantity_Locked')}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
  const hasActualQuantity = actualKey != null;
  const differsFromAdvised = hasActualQuantity && actualKey !== advisedQuantity;
  const tolerancesAvailable = hasQuantityTolerances(
    inspection?.quantityTolerancePlus,
    inspection?.quantityToleranceMinus
  );
  const withinTolerance =
    actualKey != null &&
    differsFromAdvised &&
    tolerancesAvailable &&
    isActualQuantityWithinTolerance(
      advisedQuantity,
      actualKey,
      Number(inspection?.quantityTolerancePlus),
      Number(inspection?.quantityToleranceMinus)
    );
  const outOfTolerance = differsFromAdvised && tolerancesAvailable && !withinTolerance;
  const lowerBound = advisedQuantity * (1 - Number(inspection?.quantityToleranceMinus) / 100);
  const upperBound = advisedQuantity * (1 + Number(inspection?.quantityTolerancePlus) / 100);
  const proposedSampling =
    actualKey == null
      ? null
      : resolveDeviceSampling({
          inspectionLevelValue: (inspection?.inspectionLevel as { value?: number } | undefined)
            ?.value,
          actualQuantity: actualKey,
          aqlRows,
          aqlMajor: inspection?.aqlMajor,
          aqlMinor: inspection?.aqlMinor,
          currentAllowedMajor: inspection?.allowedDefectsMaj ?? 0,
          currentAllowedMinor: inspection?.allowedDefectsMin ?? 0,
        });
  const isEditingActualQuantity = actualDraft != null;
  const showDiscrepancy = showDiscrepancyPrompt;
  const showPackingSection =
    packingVisible === null ? savedPackingShouldShow(inspection) : packingVisible;
  const showWithin =
    reviewTolerance &&
    !showDiscrepancyPrompt &&
    !isEditingActualQuantity &&
    withinTolerance &&
    toleranceChoice === 'pending' &&
    !inspection?.sampleQuantityRecalculated;
  const showOut =
    reviewTolerance &&
    !showDiscrepancyPrompt &&
    !isEditingActualQuantity &&
    outOfTolerance &&
    toleranceChoice === 'pending';
  const showWaitForFactory =
    reviewTolerance && !isEditingActualQuantity && outOfTolerance && toleranceChoice === 'wait';
  const packingPhotos = collectPackingListImages(inspection, packingPreviews);
  const updateActualQuantity = (rawValue: string): void => {
    setActualDraft(rawValue);
    if (rawValue.trim() === '' || isWholeQuantity(rawValue)) {
      setActualError('');
      return;
    }
    setActualError(translate('Please enter a valid whole number greater than 0.'));
  };
  const commitActualQuantity = (): void => {
    if (actualDraft == null) {
      return;
    }
    const rawValue = actualDraft.trim();
    if (rawValue !== '' && !isWholeQuantity(rawValue)) {
      setActualError(translate('Please enter a valid whole number greater than 0.'));
      return;
    }
    const nextQuantity = rawValue === '' ? null : Number(rawValue);
    setActualError('');
    setActualDraft(null);
    if (nextQuantity === actualKey) {
      return;
    }
    applyActualQuantity(nextQuantity);
  };
  const submitFactoryQuantity = (): void => {
    const rawValue = factoryQuantityDraft.trim();
    if (!isWholeQuantity(rawValue)) {
      setFactoryQuantityError(translate('Please enter a valid whole number greater than 0.'));
      return;
    }
    const nextQuantity = Number(rawValue);
    setFactoryQuantityDraft('');
    setFactoryQuantityError('');
    if (nextQuantity === actualKey) {
      setToleranceChoice('pending');
      return;
    }
    applyActualQuantity(nextQuantity);
  };
  const failOutOfToleranceInspection = (): void => {
    failDefectBaseline.current = countCriticalDefects(inspection?.qcDefects);
    hasOpenedFailDefect.current = false;
    sessionStorage.setItem(QUANTITY_FAIL_DEFECT_KEY, 'true');
    setToleranceChoice('fail');
    defectsChangeHandlers?.showAddDefect?.(true);
  };
  const applyActualQuantity = (nextQuantity: number | null): void => {
    onInspectionPatch?.({ actualQuantity: nextQuantity, sampleQuantityRecalculated: false });
    setToleranceChoice('pending');
    setShowDiscrepancyPrompt(false);
    setReviewTolerance(false);
    if (nextQuantity == null || nextQuantity === advisedQuantity) {
      setPackingVisible(false);
      return;
    }
    setPackingVisible(false);
    const entryPrompt = resolveQuantityEntryPrompt({
      advisedQuantity,
      actualQuantity: nextQuantity,
      overPercent: inspection?.quantityTolerancePlus,
      underPercent: inspection?.quantityToleranceMinus,
    });
    setShowDiscrepancyPrompt(entryPrompt === 'discrepancy');
    setReviewTolerance(entryPrompt !== 'none');
  };
  const updatePackingListQuantity = (rawValue: string): void => {
    setPackingDraft(rawValue);
    if (rawValue.trim() === '') {
      setPackingError('');
      setPackingDraft(null);
      onInspectionPatch?.({ packingListQuantity: null });
      return;
    }
    if (!isNonNegativeWholeQuantity(rawValue)) {
      setPackingError(translate('Please enter a whole number that is 0 or greater.'));
      return;
    }
    setPackingError('');
    setPackingDraft(null);
    onInspectionPatch?.({ packingListQuantity: Number(rawValue) });
  };
  const recalculateSample = (): void => {
    if (!proposedSampling) {
      return;
    }
    setToleranceChoice('recalc');
    onInspectionPatch?.({
      sampleSize: proposedSampling.sampleSize,
      allowedDefectsMaj: proposedSampling.allowedDefectsMaj,
      allowedDefectsMin: proposedSampling.allowedDefectsMin,
      sampleQuantityRecalculated: true,
    });
  };
  const keepBookedSample = (): void => {
    setToleranceChoice('keep');
    onInspectionPatch?.({
      sampleSize: bookedSampling.current.sampleSize,
      allowedDefectsMaj: bookedSampling.current.allowedDefectsMaj,
      allowedDefectsMin: bookedSampling.current.allowedDefectsMin,
      sampleQuantityRecalculated: false,
    });
  };
  const clearedPackingListPatch = (): {
    packingListQuantity: null;
    fpImages: Image[];
    newImages: Image[];
    fullProductImageUrls: string;
  } => {
    const packingImages = collectPackingListImages(inspection, packingPreviews);
    if (inspection?.id && packingImages.length > 0) {
      void imageStorage.removeMultipleItems(
        inspection.id,
        InspectionImageType.FULL_PRODUCT,
        packingImages.map((image) => image.id)
      );
    }
    setPackingPreviews([]);
    setPackingDraft(null);
    setPackingError('');
    return {
      packingListQuantity: null,
      fpImages: (inspection?.fpImages ?? []).filter((image) => !isPackingListImage(image)),
      newImages: (inspection?.newImages ?? []).filter((image) => !isPackingListImage(image)),
      fullProductImageUrls: withoutPackingListUrls(inspection?.fullProductImageUrls),
    };
  };
  const chooseAddPackingList = (): void => {
    setShowDiscrepancyPrompt(false);
    setPackingVisible(true);
    onInspectionPatch?.({
      inspectorComment: removeNoPackingListComment(inspection?.inspectorComment),
    });
  };
  const chooseNoPackingList = (): void => {
    onInspectionPatch?.({
      ...clearedPackingListPatch(),
      inspectorComment: appendNoPackingListComment(inspection?.inspectorComment),
    });
    setShowDiscrepancyPrompt(false);
    setPackingVisible(false);
  };
  const chooseSkipPackingList = (): void => {
    onInspectionPatch?.({
      ...clearedPackingListPatch(),
      inspectorComment: removeNoPackingListComment(inspection?.inspectorComment),
    });
    setShowDiscrepancyPrompt(false);
    setPackingVisible(false);
  };
  const abandonOutOfToleranceInspection = async (): Promise<void> => {
    if (!inspection?.id || isAbandoning) {
      return;
    }
    if (!isOnline) {
      toast.error(translate('A connection is required to abandon this inspection.'));
      return;
    }
    setIsAbandoning(true);
    let didAbandon = false;
    try {
      const abandonResult = await requestInspectionAbandon(inspection.id);
      if (!abandonResult.ok) {
        toast.error(translate(abandonResult.messageKey), { toastId: ABANDON_FAILED_TOAST_ID });
        return;
      }
      await savedInspectionsStorageV2.delete(inspection.id, InspectionOwner.UI);
      const remainingInspections = store
        .getState()
        .inspectionsData.inspections.filter((item) => item.id !== inspection.id);
      store.dispatch(setInspectionList(remainingInspections));
      store.dispatch(closeInspection(inspection.id));
      didAbandon = true;
      navigate(ROUTES.INSPECTIONS, { replace: true });
    } catch (error) {
      Sentry.captureException(error);
      toast.error(translate('Abandon_Inspection_Request_Failed'), {
        toastId: ABANDON_FAILED_TOAST_ID,
      });
    } finally {
      if (!didAbandon) {
        setIsAbandoning(false);
      }
    }
  };
  const actualDescribedBy = ['actual-quantity-help', actualError ? 'actual-quantity-error' : '']
    .filter(Boolean)
    .join(' ');
  return (
    <div className={styles.formContainer}>
      <div className={styles.inputRow}>
        <div className={styles.inputGroup}>
          <label className={styles.label} htmlFor="advisedQuantity">
            {translate('Advised Quantity')}
          </label>
          <input
            className={styles.input}
            id="advisedQuantity"
            name="lotSize"
            value={inspection?.lotSize ?? ''}
            disabled
            readOnly
          />
        </div>
        <div className={`${styles.inputGroup} ${styles.actualQuantityGroup}`}>
          <label className={styles.label} htmlFor="actualQuantity">
            {translate('Actual Quantity')}
          </label>
          <input
            className={`${styles.input} ${actualError ? styles.inputInvalid : ''}`}
            type="text"
            inputMode="numeric"
            id="actualQuantity"
            name="actualQuantity"
            placeholder={translate('Enter actual inspected quantity')}
            aria-invalid={actualError ? true : undefined}
            aria-describedby={actualDescribedBy}
            value={actualDraft ?? inspection?.actualQuantity ?? ''}
            onChange={(event) => updateActualQuantity(event.target.value)}
            onBlur={commitActualQuantity}
            disabled={isQuantityLocked}
          />
          <p id="actual-quantity-help" className={styles.quantityHint}>
            {isQuantityLocked
              ? translate('Actual_Quantity_Locked')
              : interpolate(
                  translate('Leave blank to use the booked quantity ({0}). Whole numbers only.'),
                  [formatQuantity(advisedQuantity)]
                )}
          </p>
          {actualError && (
            <p id="actual-quantity-error" className={styles.error} role="alert">
              {actualError}
            </p>
          )}
        </div>
      </div>
      {showPackingSection && (
        <section className={styles.packingSection} aria-labelledby="packing-list-heading">
          <h3 id="packing-list-heading" className={styles.sectionTitle}>
            {translate('Add Packing List Info')}
          </h3>
          <div className={styles.packingFields}>
            <div className={styles.inputGroup}>
              <label className={styles.label} htmlFor="packingListQuantity">
                {translate('Packing List Quantity')}
              </label>
              <input
                className={`${styles.input} ${packingError ? styles.inputInvalid : ''}`}
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                id="packingListQuantity"
                placeholder={translate('Enter quantity from packing list')}
                aria-invalid={packingError ? true : undefined}
                aria-describedby={packingError ? 'packing-list-error' : undefined}
                value={packingDraft ?? inspection?.packingListQuantity ?? ''}
                onKeyDown={(event) => {
                  if (
                    event.key === '-' ||
                    event.key === 'e' ||
                    event.key === 'E' ||
                    event.key === '.'
                  ) {
                    event.preventDefault();
                  }
                }}
                onChange={(event) => updatePackingListQuantity(event.target.value)}
              />
              {packingError && (
                <p id="packing-list-error" className={styles.error} role="alert">
                  {packingError}
                </p>
              )}
            </div>
            <div className={styles.inputGroup}>
              <p className={styles.label} id="packing-list-photo-label">
                {translate('Packing List Photo')}
              </p>
              <button
                type="button"
                className={styles.photoButton}
                onClick={() => fileInputRef.current?.click()}
              >
                {translate('Take / Upload Photo')}
              </button>
              <input
                ref={fileInputRef}
                className={styles.fileInput}
                type="file"
                accept="image/*"
                capture="environment"
                aria-labelledby="packing-list-photo-label"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) {
                    void addPackingListPhoto(file);
                  }
                  event.target.value = '';
                }}
              />
              {packingPhotos.length > 0 && (
                <ul className={styles.previewList}>
                  {packingPhotos.map((image) => (
                    <PackingListPhotoPreview
                      key={image.id}
                      image={image}
                      inspectionId={inspection?.id}
                      alt={image.name || translate('Packing List Photo')}
                    />
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      )}
      {inspection?.sampleQuantityRecalculated && (
        <p className={styles.helper}>
          {translate('Sample quantity was recalculated from the actual quantity.')}{' '}
          {translate('Sample quantity')}: {inspection.sampleSize}
        </p>
      )}
      {showDiscrepancy && (
        <QuantityPrompt
          titleId="quantity-discrepancy-title"
          title={translate('Quantity Discrepancy Detected')}
          message={interpolate(
            translate(
              'The actual quantity ({0}) differs from the booked quantity ({1}). Would you like to record the packing list quantity and take a photo?'
            ),
            [formatQuantity(actualKey ?? 0), formatQuantity(advisedQuantity)]
          )}
          actions={[
            {
              id: 'add-packing-list',
              label: translate('Add Packing List Info'),
              tone: 'primary',
              onClick: chooseAddPackingList,
            },
            {
              id: 'no-packing-list',
              label: translate('No Packing List Available'),
              tone: 'secondary',
              onClick: chooseNoPackingList,
            },
            ...(shouldShowSkipQuantityDiscrepancy(withinTolerance)
              ? [
                  {
                    id: 'skip-packing-list',
                    label: translate('Skip & Continue'),
                    tone: 'quiet' as const,
                    onClick: chooseSkipPackingList,
                  },
                ]
              : []),
          ]}
        />
      )}
      {showWithin && (
        <QuantityPrompt
          titleId="quantity-within-title"
          title={translate('Quantity Within Tolerance')}
          message={interpolate(
            translate(
              'The actual quantity ({0}) is within the allowable tolerance range ({1} – {2}). Would you like to recalculate the sample size based on the actual quantity?'
            ),
            [formatQuantity(actualKey), formatQuantity(lowerBound), formatQuantity(upperBound)]
          )}
          actions={[
            {
              id: 'recalculate-sample',
              label: translate('Recalculate Sample Size'),
              tone: 'primary',
              onClick: recalculateSample,
              disabled: !proposedSampling,
            },
            {
              id: 'keep-sample',
              label: translate('Keep Original Sample Size'),
              tone: 'secondary',
              onClick: keepBookedSample,
            },
          ]}
        >
          <dl className={styles.figures}>
            <div>
              <dt>{translate('Current Sample Size')}</dt>
              <dd>{bookedSampling.current.sampleSize ?? inspection?.sampleSize}</dd>
            </div>
            {proposedSampling && (
              <div>
                <dt>{translate('New Sample Size')}</dt>
                <dd>{proposedSampling.sampleSize}</dd>
              </div>
            )}
          </dl>
          {!proposedSampling && (
            <p className={styles.error} role="alert">
              {translate('Sample quantity could not be recalculated for this actual quantity.')}
            </p>
          )}
        </QuantityPrompt>
      )}
      {showOut && (
        <QuantityPrompt
          titleId="quantity-out-title"
          title={translate('Quantity Out of Tolerance')}
          message={interpolate(
            translate(
              'The entered quantity ({0}) exceeds the allowable tolerance limit ({1} – {2}) for this inspection.'
            ),
            [formatQuantity(actualKey), formatQuantity(lowerBound), formatQuantity(upperBound)]
          )}
          actions={[
            {
              id: 'wait-for-factory',
              label: translate('Wait for Factory'),
              tone: 'primary',
              description: translate(
                'Only the actual quantity can be changed while the factory recounts. The quantity check runs again for the new quantity.'
              ),
              onClick: () => setToleranceChoice('wait'),
            },
            {
              id: 'fail-inspection',
              label: translate('Fail Inspection'),
              tone: 'secondary',
              description: translate(
                'Log a Critical defect with its code, description, and photo. You can continue the inspection afterwards.'
              ),
              onClick: failOutOfToleranceInspection,
            },
            {
              id: 'abandon-inspection',
              label: translate('Abandoned Inspection'),
              tone: 'danger',
              disabled: isAbandoning,
              description: translate(
                'The inspection date and assigned inspector will be cleared. The status returns to Planned, this inspection closes, and you return to the inspections list.'
              ),
              onClick: () => {
                void abandonOutOfToleranceInspection();
              },
            },
          ]}
        />
      )}
      {showWaitForFactory && (
        <QuantityPrompt
          titleId="quantity-wait-title"
          title={translate('Waiting for Factory')}
          message={translate(
            'Enter the actual quantity once the factory confirms the count. The quantity check runs again for the new quantity.'
          )}
          actions={[
            {
              id: 'update-factory-quantity',
              label: translate('Update Quantity'),
              tone: 'primary',
              onClick: submitFactoryQuantity,
            },
          ]}
        >
          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="factoryActualQuantity">
              {translate('Updated Actual Quantity')}
            </label>
            <input
              className={`${styles.input} ${factoryQuantityError ? styles.inputInvalid : ''}`}
              type="text"
              inputMode="numeric"
              id="factoryActualQuantity"
              placeholder={translate('Enter actual inspected quantity')}
              aria-invalid={factoryQuantityError ? true : undefined}
              aria-describedby={factoryQuantityError ? 'factory-quantity-error' : undefined}
              value={factoryQuantityDraft}
              onChange={(event) => {
                setFactoryQuantityDraft(event.target.value);
                setFactoryQuantityError('');
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  submitFactoryQuantity();
                }
              }}
            />
            {factoryQuantityError && (
              <p id="factory-quantity-error" className={styles.error} role="alert">
                {factoryQuantityError}
              </p>
            )}
          </div>
        </QuantityPrompt>
      )}
    </div>
  );
};

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

const PackingListPhotoPreview: React.FC<{ image: Image; inspectionId?: string; alt: string }> = ({
  image,
  inspectionId,
  alt,
}) => {
  const [src, setSrc] = useState(image.data || image.url || image.uploadedUrl || '');
  useEffect(() => {
    const initial = image.data || image.url || image.uploadedUrl;
    if (initial) {
      setSrc(initial);
      return;
    }
    if (!inspectionId || !image.id) {
      return;
    }
    let isCurrent = true;
    imageStorage
      .getImage(inspectionId, image.type || InspectionImageType.FULL_PRODUCT, image.id)
      .then((data) => {
        if (isCurrent && data) {
          setSrc(data);
        }
      });
    return () => {
      isCurrent = false;
    };
  }, [image.data, image.id, image.type, image.uploadedUrl, image.url, inspectionId]);
  if (!src) {
    return null;
  }
  return (
    <li>
      <img src={src} alt={alt} />
    </li>
  );
};
